import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { tradeLicenseApi } from '../../../services/tradeLicenseApi';
import ListPagination from '../../../components/Office/ListPagination';

const PAGE_SIZE = 20;

const FILTER_OPTIONS = [
  { value: '', label: 'All Applications' },
  { value: 'submitted', label: 'New Applications' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
  { value: 'issued', label: 'License Issued' },
  { value: 'rejected', label: 'Rejected' },
];

const ApplicationsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFromUrl = searchParams.get('status') || '';
  const paymentStatusFromUrl = searchParams.get('payment_status') || '';
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(statusFromUrl);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ count: 0, next: null, previous: null });

  useEffect(() => {
    setFilter(statusFromUrl);
    setPage(1);
  }, [statusFromUrl, paymentStatusFromUrl]);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
        ...(filter ? { status: filter } : {}),
        ...(paymentStatusFromUrl ? { payment_status: paymentStatusFromUrl } : {}),
      };
      const response = await tradeLicenseApi.getApplications(params);
      const data = response.data;
      setApplications(data.results || data);
      setPagination({
        count: data.count ?? (Array.isArray(data) ? data.length : 0),
        next: data.next ?? null,
        previous: data.previous ?? null,
      });
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      setApplications([]);
      setPagination({ count: 0, next: null, previous: null });
    } finally {
      setLoading(false);
    }
  }, [filter, paymentStatusFromUrl, page]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setPage(1);
    if (newFilter) {
      setSearchParams({
        status: newFilter,
        ...(paymentStatusFromUrl ? { payment_status: paymentStatusFromUrl } : {}),
      });
    } else {
      setSearchParams(paymentStatusFromUrl ? { payment_status: paymentStatusFromUrl } : {});
    }
  };

  const clearPaymentFilter = () => {
    setSearchParams(filter ? { status: filter } : {});
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Trade License Applications</h1>
      </div>

      {paymentStatusFromUrl && (
        <div style={{ marginBottom: '12px', padding: '10px 12px', background: '#fff8e1', borderRadius: 6, color: '#6d4c00' }}>
          Showing applications pending payment collection.
          <button type="button" className="btn btn-secondary btn-sm" style={{ marginLeft: 10 }} onClick={clearPaymentFilter}>
            Clear Payment Filter
          </button>
        </div>
      )}

      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        {FILTER_OPTIONS.map((opt) => (
          <button
            key={opt.value || 'all'}
            onClick={() => handleFilterChange(opt.value)}
            className={`btn ${filter === opt.value ? 'btn-primary' : 'btn-secondary'}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : (
        <>
          <div className="card">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #ddd' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Application #</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Business Name</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Applicant</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Assigned To</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Current Stage</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Submitted</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {applications.length > 0 ? (
                  applications.map((app) => (
                    <tr key={app.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '12px' }}>
                        <strong>{app.application_number}</strong>
                      </td>
                      <td style={{ padding: '12px' }}>{app.business_name_en}</td>
                      <td style={{ padding: '12px' }}>{app.applicant_email}</td>
                      <td style={{ padding: '12px' }}>{app.assigned_to_name || app.assigned_to_username || '—'}</td>
                      <td style={{ padding: '12px' }}>{app.current_stage_name || '—'}</td>
                      <td style={{ padding: '12px' }}>
                        <span className={`status-badge ${app.status}`}>{app.status}</span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        {app.submitted_date
                          ? new Date(app.submitted_date).toLocaleDateString()
                          : new Date(app.created_at).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <Link to={`/office/applications/${app.id}`} className="btn btn-primary">
                          View
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                      No applications found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <ListPagination
            count={pagination.count}
            next={pagination.next}
            previous={pagination.previous}
            currentPage={page}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            loading={loading}
          />
        </>
      )}
    </div>
  );
};

export default ApplicationsList;
